/**
 * Where a signed-out visitor was going, kept until they have signed in (and set up their
 * voice, when that comes first), as the apps hold a notification's or App Link's result.
 *
 * Only a meeting's notes: a `/session/<id>/result` link (the notes-ready email, or an old
 * `/?session=` link once it is mapped there) opened signed out lands on sign-in, and this is
 * what takes the person on to it afterwards instead of Home.
 *
 * `localStorage` rather than `sessionStorage`, because the emailed sign-in link opens in a
 * new tab. So it expires ([RETURN_TTL_MS]), it is taken once, it is dropped on sign-out,
 * and only a same-origin result path is ever stored or returned: nothing read back can steer
 * the app anywhere else. Storage that refuses all this just lands on Home, as before.
 */

/** Named like the app's other keys (`meetfolks-theme`, `meetfolks-welcome-done`). */
export const RETURN_KEY = "meetfolks-return-to";

/** How long a remembered destination lasts: long enough to fetch a sign-in email. */
export const RETURN_TTL_MS = 60 * 60 * 1000;

/** A result path: the server's session id (`ses_` + 24 hex), optionally with its tab. */
const RESULT_PATH = /^\/session\/ses_[0-9a-f]{24}\/result(\?tab=[a-z_]{1,32})?$/;

/** The result path this location is, or null for anything else. */
export function resultPathOf(location: Pick<Location, "pathname" | "search">): string | null {
  const path = location.pathname + location.search;
  return RESULT_PATH.test(path) ? path : null;
}

/** Remember [path] for after sign-in. Anything but a result path is ignored. */
export function rememberReturnTo(
  path: string,
  now: number = Date.now(),
  storage: Pick<Storage, "setItem"> | null = safeStorage()
): void {
  if (!RESULT_PATH.test(path)) return;
  try {
    storage?.setItem(RETURN_KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Lands on Home after sign-in, which is all a blocked storage costs.
  }
}

/**
 * The remembered destination, taken: it is removed whether or not it is still good. Null
 * when there is none, it has expired, or it is not a result path.
 */
export function takeReturnTo(
  now: number = Date.now(),
  storage: Pick<Storage, "getItem" | "removeItem"> | null = safeStorage()
): string | null {
  try {
    const raw = storage?.getItem(RETURN_KEY);
    if (!raw) return null;
    storage?.removeItem(RETURN_KEY);
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof path !== "string" || typeof at !== "number") return null;
    if (!RESULT_PATH.test(path)) return null;
    const age = now - at;
    return age >= 0 && age <= RETURN_TTL_MS ? path : null;
  } catch {
    return null;
  }
}

/** Forget it: signing out ends whatever the last person was on their way to. */
export function clearReturnTo(storage: Pick<Storage, "removeItem"> | null = safeStorage()): void {
  try {
    storage?.removeItem(RETURN_KEY);
  } catch {
    // Nothing to clear in a storage that refuses access.
  }
}

/** `localStorage`, or null where even reading the property throws (some private modes). */
function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
